import React, { FC } from 'react';
import { Button, Heading, Image, TrackingPropsInterface, Types } from '@marriott/mi-ui-library';

import { WalletSavedCardsSectionModel } from '../../WalletSavedCardsSection.types';
import { WALLET_SAVED_CARDS_TEST_IDS } from '../../WalletSavedCardsSection.constants';
import { StyledBoundlessBenefitsBanner } from './BoundlessBenefitsBanner.styles';

export type BoundlessBenefitsBannerProps = {
  boundless: NonNullable<WalletSavedCardsSectionModel['boundless']>;
  trackingProperties?: TrackingPropsInterface;
  customClickTrackValue?: string;
  testId?: string;
};

export const BoundlessBenefitsBanner: FC<BoundlessBenefitsBannerProps> = ({
  boundless,
  trackingProperties,
  customClickTrackValue,
  testId = WALLET_SAVED_CARDS_TEST_IDS.boundlessBanner,
}) => {
  const {
    sectionHeading,
    image,
    imageAltText,
    benefits,
    ctaLinkLabel,
    ctaLink,
    ctaLinkOpenInANewTab,
    ctaLinkOpenInNewTab,
  } = boundless;

  const opensInNewTab = Boolean(ctaLinkOpenInNewTab ?? ctaLinkOpenInANewTab);
  const hasBenefits = Boolean(benefits?.length);

  if (!sectionHeading && !image && !hasBenefits && !ctaLinkLabel) {
    return null;
  }

  return (
    <StyledBoundlessBenefitsBanner
      data-testid={testId}
      data-component-name="m-account-BoundlessBenefitsBanner"
      className="wallet-boundless-benefits-banner"
    >
      {image ? (
        <div className="wallet-boundless-benefits-banner__image-wrapper">
          <Image
            customClass="wallet-boundless-benefits-banner__image"
            altText={imageAltText || ''}
            defaultImageURL={image}
          />
        </div>
      ) : null}

      <div className="wallet-boundless-benefits-banner__content">
        {sectionHeading ? (
          <Heading
            variation={Types.headingType.subtitle}
            fontSize={Types.size.large}
            titleText={sectionHeading}
            customClass="wallet-boundless-benefits-banner__heading m-0"
          />
        ) : null}

        {ctaLinkLabel && ctaLink ? (
          <Button
            isLink={true}
            type={Types.ButtonTypeVariation.Button}
            className={`${
              opensInNewTab ? 'm-link-tertiary-button-external' : 'm-link-tertiary-button'
            } wallet-boundless-benefits-banner__cta p-0 flex-shrink-0`}
            href={ctaLink}
            target={opensInNewTab ? '_blank' : '_self'}
            rel={opensInNewTab ? 'noopener noreferrer' : undefined}
            buttonCopy={ctaLinkLabel}
            ariaLabel={ctaLinkLabel}
            trackingProperties={trackingProperties}
            custom_click_track_value={customClickTrackValue}
            testId={WALLET_SAVED_CARDS_TEST_IDS.boundlessBannerCta}
          />
        ) : null}

        {hasBenefits ? (
          <ul className="wallet-boundless-benefits-banner__benefits">
            {benefits?.map((benefit, index) => (
              <li
                key={`${benefit.multiplier ?? 'benefit'}-${index}`}
                className="wallet-boundless-benefits-banner__benefit"
                data-testid={WALLET_SAVED_CARDS_TEST_IDS.boundlessBannerBenefit}
              >
                {benefit.multiplier ? (
                  <span className="wallet-boundless-benefits-banner__multiplier">
                    <span className="wallet-boundless-benefits-banner__multiplier-value t-subtitle-s">
                      {benefit.multiplier}
                    </span>
                    <span className="wallet-boundless-benefits-banner__multiplier-accent" aria-hidden="true" />
                  </span>
                ) : null}
                {benefit.description ? (
                  <p className="wallet-boundless-benefits-banner__description t-font-s m-0">{benefit.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </StyledBoundlessBenefitsBanner>
  );
};
